import dayjs from "dayjs";

import { diDecorator } from "@/backend/di/diDecorator";
import type { PaymentPlanRepository } from "@/backend/domains/subscription/domain/paymentPlan.repository";
import type { SubscriptionRepository } from "@/backend/domains/subscription/domain/subscription.repository";
import { BaseUpdatableService } from "@/backend/shared/application/base.service";

import { appErrors } from "@/errors";
import { doIt } from "@/utils/doIt";
import pMap from "@/utils/pMap";

import { UsageLimits } from "../../subscription/domain/PaymentPlan";
import { ELimitType, ILimit, ILimitCreating } from "../domain/Limit";
import type { LimitRepository } from "../domain/limit.repository";

//

export interface LimitsData {
  id: string;
  organizationId: string;
  limitType: ELimitType;
  currentValue: number;
  resetDate: Date | null;
  usageLimit: number | false | null;
  remaining: number;
}

//

@diDecorator.injectable()
export class LimitService extends BaseUpdatableService<ILimit, ILimitCreating> {
  constructor(
    @diDecorator.inject("LimitRepository")
    public repository: LimitRepository,
    @diDecorator.inject("SubscriptionRepository")
    private subscriptionRepository: SubscriptionRepository,
    @diDecorator.inject("PaymentPlanRepository")
    private paymentPlanRepository: PaymentPlanRepository,
  ) {
    super(repository);
  }

  //

  public async getOneLimit(
    organizationId: string,
    limitType: ELimitType,
    session?: unknown,
  ): Promise<LimitsData> {
    const limit = await this.getOrCreateOne(organizationId, limitType, session);
    const plan = await this.subscriptionRepository.getPlanByOrganization(organizationId);
    const usageLimit = await this.paymentPlanRepository.getUsageLimit(plan, limitType);
    const remaining = this.calcRemaining(usageLimit, limit.currentValue);
    return { ...limit, remaining, usageLimit };
  }

  //

  public async getEveryLimit(organizationId: string, session?: unknown) {
    return await this.getOrCreateAll(organizationId, session);
  }

  //

  public calcRemainingForManyLimits(limits: ILimit[], usageLimits: UsageLimits) {
    return limits.map((limit) => {
      const usageLimit = usageLimits[limit.limitType];
      const remaining = this.calcRemaining(usageLimit, limit.currentValue);
      return { ...limit, remaining, usageLimit };
    });
  }

  //

  public async createAll(organizationId: string, session?: unknown): Promise<void> {
    await this.getOrCreateAll(organizationId, session);
  }

  //

  public async setUsage(
    organizationId: string,
    limitType: ELimitType,
    value: number,
    session?: unknown,
  ): Promise<ILimit> {
    const limitData = await this.getOneLimit(organizationId, limitType, session);
    return await this.repository.requireByIdAndUpdate(
      limitData.id,
      { currentValue: value },
      { session },
    );
  }

  //

  public async increaseUsage(
    organizationId: string,
    limitType: ELimitType,
    value: number = 1,
    session?: unknown,
  ): Promise<ILimit> {
    const limitData = await this.getOneLimit(organizationId, limitType, session);
    const newValue = limitData.currentValue + value;
    const newRemaining = this.calcRemaining(limitData.usageLimit, newValue);
    if (newRemaining < 0) throw new appErrors.LimitExceededError(limitType);
    return await this.repository.requireByIdAndUpdate(
      limitData.id,
      { currentValue: newValue },
      { session },
    );
  }

  //

  private async createEmpty(
    organizationId: string,
    limitType: ELimitType,
    session: unknown | undefined,
  ) {
    return await this.repository.create(
      { limitType, organizationId, currentValue: 0, resetDate: null },
      session,
    );
  }

  //

  private async getOrCreateOne(
    organizationId: string,
    limitType: ELimitType,
    session: unknown | undefined,
  ) {
    const limit = await doIt(async () => {
      const limit = await this.repository.findOne({ organizationId, limitType }, { session });
      if (limit) return limit;
      return await this.createEmpty(organizationId, limitType, session);
    });
    return await this.updateResetDate(limit, session);
  }

  //

  private async getOrCreateAll(organizationId: string, _session: unknown | undefined) {
    return await this.repository.startSession(async (s) => {
      const session = _session ?? s;

      const limits = await doIt(async () => {
        const limits = await this.repository.find({ organizationId }, { session });

        const limitTypeToCreate = Object.values(ELimitType).filter(
          (v) => limits.findIndex((l) => l.limitType === v) === -1,
        );

        const created = await pMap(limitTypeToCreate, (type) =>
          this.createEmpty(organizationId, type, session),
        );

        return [...limits, ...created];
      });

      return await pMap(limits, (d) => this.updateResetDate(d, session));
    });
  }

  //

  private async updateResetDate(limit: ILimit, session: unknown | undefined) {
    const resetDate = limit.resetDate;
    const overdue = resetDate ? new Date() > resetDate : false;

    if (!overdue) return limit;

    const nextResetDate = this.getNextResetDate(limit.limitType);

    return await this.repository.requireByIdAndUpdate(
      limit.id,
      { currentValue: 0, resetDate: nextResetDate },
      { session },
    );
  }

  //

  private getNextResetDate(limitType: ELimitType) {
    const currentDate = new Date();
    const map: Record<ELimitType, Date | null> = {
      [ELimitType.products]: null,
      [ELimitType.ordersPerMonth]: dayjs(currentDate).add(1, "month").toDate(),
      [ELimitType.offerConnectsPerDay]: new Date(currentDate.setHours(23, 59, 59, 999)),
      [ELimitType.offerConnectsPerMonth]: dayjs(currentDate).add(1, "month").toDate(),
      [ELimitType.cabinets]: null,
    };
    return map[limitType];
  }

  //

  private calcRemaining(usageLimit: number | null | false, currentValue: number) {
    if (usageLimit === null) return Infinity;
    if (usageLimit === false) return 0;
    const remaining = usageLimit - currentValue;
    // return remaining < 0 ? 0 : remaining; // Так упускается отрицательное значение
    return remaining;
  }
}
